import { useMemo } from 'react';
import { useTheme as muiUseTheme } from '@mui/material/styles';
import { getLightTheme } from './themes/light';
import { getDarkTheme } from './themes/dark';
import { useBrandingOverrides } from './brandingContext';
import { themeDeepMerge } from './utils/themeDeepMerge';
import { paletteTokens } from './generated/paletteVariables';
const themeGetters = {
    light: getLightTheme,
    dark: getDarkTheme
};
/**
 * Unbranded base themes are pure functions of their palette argument, so the
 * no-override result is stable for a given theme name. Build each variant
 * lazily, once, and reuse it — instead of calling `createTheme()` on every
 * render or whenever unrelated context changes.
 */ const baseThemeCache = {};
const getBaseTheme = (themeName)=>{
    baseThemeCache[themeName] ??= themeGetters[themeName]();
    return baseThemeCache[themeName];
};
/**
 * Hook to access the current theme or a specific theme variant.
 *
 * @param themeName - Optional. If provided, returns the branded version of that
 *   theme variant. If omitted, returns the current theme from MUI context.
 *
 * When a themeName is specified, the hook will:
 * 1. Get branding overrides from BrandingContext (if available)
 * 2. Apply those overrides to the requested theme variant
 * 3. Return the branded theme
 *
 * The by-name path intentionally does not depend on the MUI context theme: a
 * requested variant is derived from its own tokens (plus branding), so context
 * churn must not rebuild it.
 */ export function useTheme(themeName) {
    const contextTheme = muiUseTheme();
    const branding = useBrandingOverrides();
    const namedTheme = useMemo(()=>{
        if (themeName === undefined) {
            return undefined;
        }
        const overridePalette = branding?.overridePalette;
        const overrideTheme = branding?.overrideTheme;
        if (!overridePalette && !overrideTheme) {
            return getBaseTheme(themeName);
        }
        const palette = overridePalette ? themeDeepMerge(paletteTokens, overridePalette) : undefined;
        const baseTheme = themeGetters[themeName](palette);
        if (!overrideTheme) {
            return baseTheme;
        }
        return themeDeepMerge(baseTheme, overrideTheme);
    }, [
        themeName,
        branding
    ]);
    return themeName === undefined ? contextTheme : namedTheme;
}

//# sourceMappingURL=useTheme.js.map